import React, { useState, useEffect, useRef } from 'react';
import './CertificateModal.css';

/**
 * CertificateModal
 * Props:
 *   type     – 'booking' | 'appointment'
 *   project  – project name string
 *   onClose  – close handler
 */
export default function CertificateModal({ type, project, onClose }) {
  const today = new Date();
  const dateStr = today.toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' });
  const refNum = `PG-${type === 'booking' ? 'BK' : 'AP'}-${Date.now().toString().slice(-6)}`;
  const certRef = useRef(null);

  const [form, setForm] = useState({
    name: '', phone: '', email: '',
    unit: '', date: today.toISOString().split('T')[0], time: '11:00',
  });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const handlePrint = () => {
    const content = certRef.current;
    const win = window.open('', '_blank', 'width=900,height=700');
    win.document.write(`
      <html>
        <head>
          <title>${type === 'booking' ? 'Booking' : 'Appointment'} Certificate – ${project}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: 'Georgia', serif; background: #fff; }
            ${getCertPrintStyles()}
          </style>
        </head>
        <body>${content.innerHTML}</body>
      </html>
    `);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); win.close(); }, 400);
  };

  const isBooking = type === 'booking';

  return (
    <div className="cert-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="cert-modal">
        {/* Header */}
        <div className="cert-modal-header">
          <div className="cert-modal-title">
            <i className={isBooking ? 'fa fa-certificate' : 'fa fa-calendar-check'} />
            <span>
              {isBooking ? 'Booking Certificate' : 'Appointment Certificate'} — {project}
            </span>
          </div>
          <button className="cert-close" onClick={onClose}><i className="fa fa-times" /></button>
        </div>

        <div className="cert-body">
          {!submitted ? (
            /* ── FORM ── */
            <div className="cert-form-wrap">
              <p className="cert-form-intro">
                {isBooking
                  ? 'Fill in your details to generate an official Booking Certificate.'
                  : 'Fill in your details to schedule & print your Appointment Certificate.'}
              </p>
              <div className="cert-form-grid">
                <div className="cert-field">
                  <label>Full Name *</label>
                  <input
                    type="text"
                    placeholder="Your full name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </div>
                <div className="cert-field">
                  <label>Phone *</label>
                  <input
                    type="tel"
                    placeholder="+91 XXXXX XXXXX"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  />
                </div>
                <div className="cert-field">
                  <label>Email</label>
                  <input
                    type="email"
                    placeholder="your@email.com"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                </div>
                <div className="cert-field">
                  <label>{isBooking ? 'Unit / Plot Interest' : 'Project / Unit of Interest'}</label>
                  <input
                    type="text"
                    placeholder={isBooking ? 'e.g. 330 sq.ft Studio' : 'e.g. 100 Sq.Yd Villa'}
                    value={form.unit}
                    onChange={(e) => setForm({ ...form, unit: e.target.value })}
                  />
                </div>
                <div className="cert-field">
                  <label>{isBooking ? 'Booking Date' : 'Appointment Date'}</label>
                  <input
                    type="date"
                    value={form.date}
                    min={today.toISOString().split('T')[0]}
                    onChange={(e) => setForm({ ...form, date: e.target.value })}
                  />
                </div>
                {!isBooking && (
                  <div className="cert-field">
                    <label>Preferred Time</label>
                    <select value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })}>
                      {['10:00','10:30','11:00','11:30','12:00','12:30','14:00','14:30','15:00','15:30','16:00','16:30','17:00'].map(t => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              <button
                className="cert-generate-btn"
                disabled={!form.name || !form.phone}
                onClick={() => setSubmitted(true)}
              >
                <i className="fa fa-file-alt" /> Generate Certificate
              </button>
            </div>
          ) : (
            /* ── CERTIFICATE PREVIEW ── */
            <div className="cert-preview-wrap">
              <div className="cert-preview-actions">
                <button className="cert-print-btn" onClick={handlePrint}>
                  <i className="fa fa-print" /> Print Certificate
                </button>
                <button className="cert-edit-btn" onClick={() => setSubmitted(false)}>
                  <i className="fa fa-edit" /> Edit Details
                </button>
              </div>

              {/* The printable certificate */}
              <div className="cert-document" ref={certRef}>
                <div className="cert-border-outer">
                  <div className="cert-border-inner">

                    {/* Logo & Company */}
                    <div className="cert-company-header">
                      <div className="cert-logo-wrap">
                        <img src="/image/logo.png" alt="Paradise Group NCR" className="cert-logo" />
                      </div>
                      <div className="cert-company-info">
                        <div className="cert-company-name">Paradise Group NCR</div>
                        <div className="cert-company-tagline">Your Dream. Your Paradise.</div>
                        <div className="cert-company-contact">
                          📞 +91 7477-471-006 &nbsp;|&nbsp; 🌐 www.paradisegroupncr.com
                        </div>
                      </div>
                    </div>

                    <div className="cert-divider-gold" />

                    {/* Certificate Title */}
                    <div className="cert-title-block">
                      <div className="cert-watermark">{isBooking ? 'BOOKING' : 'APPOINTMENT'}</div>
                      <div className="cert-title-label">CERTIFICATE OF</div>
                      <div className="cert-title-main">
                        {isBooking ? 'PROPERTY BOOKING' : 'SITE VISIT APPOINTMENT'}
                      </div>
                      <div className="cert-ref-row">
                        <span className="cert-ref">Ref No: {refNum}</span>
                        <span className="cert-date-issued">Issued: {dateStr}</span>
                      </div>
                    </div>

                    <div className="cert-divider-gold thin" />

                    {/* Body Text */}
                    <div className="cert-body-text">
                      {isBooking ? (
                        <p>
                          This is to certify that <strong>{form.name}</strong> has expressed
                          interest in booking a unit at <strong>{project}</strong>.
                          This certificate acknowledges the booking enquiry placed on{' '}
                          <strong>{new Date(form.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}</strong>.
                        </p>
                      ) : (
                        <p>
                          This is to certify that an appointment has been scheduled for{' '}
                          <strong>{form.name}</strong> to visit <strong>{project}</strong> on{' '}
                          <strong>{new Date(form.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}</strong>
                          {' '}at <strong>{form.time} hrs</strong>.
                        </p>
                      )}
                    </div>

                    {/* Details Table */}
                    <div className="cert-details-grid">
                      <div className="cert-detail-row">
                        <span className="cert-detail-label">Name</span>
                        <span className="cert-detail-val">{form.name}</span>
                      </div>
                      <div className="cert-detail-row">
                        <span className="cert-detail-label">Phone</span>
                        <span className="cert-detail-val">{form.phone}</span>
                      </div>
                      {form.email && (
                        <div className="cert-detail-row">
                          <span className="cert-detail-label">Email</span>
                          <span className="cert-detail-val">{form.email}</span>
                        </div>
                      )}
                      <div className="cert-detail-row">
                        <span className="cert-detail-label">Project</span>
                        <span className="cert-detail-val">{project}</span>
                      </div>
                      {form.unit && (
                        <div className="cert-detail-row">
                          <span className="cert-detail-label">{isBooking ? 'Unit Interest' : 'Unit'}</span>
                          <span className="cert-detail-val">{form.unit}</span>
                        </div>
                      )}
                      <div className="cert-detail-row">
                        <span className="cert-detail-label">
                          {isBooking ? 'Booking Date' : 'Appointment Date'}
                        </span>
                        <span className="cert-detail-val">
                          {new Date(form.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}
                          {!isBooking && ` at ${form.time} hrs`}
                        </span>
                      </div>
                      <div className="cert-detail-row">
                        <span className="cert-detail-label">Reference No.</span>
                        <span className="cert-detail-val">{refNum}</span>
                      </div>
                    </div>

                    <div className="cert-divider-gold thin" />

                    {/* Signature Section */}
                    <div className="cert-signatures">
                      <div className="cert-sig-block">
                        <div className="cert-sig-line" />
                        <div className="cert-sig-name">Authorized Signatory</div>
                        <div className="cert-sig-title">Paradise Group NCR</div>
                      </div>
                      <div className="cert-stamp">
                        <div className="cert-stamp-circle">
                          <div className="cert-stamp-inner">
                            <div className="cert-stamp-pg">PG</div>
                            <div className="cert-stamp-ncr">NCR</div>
                          </div>
                        </div>
                      </div>
                      <div className="cert-sig-block right">
                        <div className="cert-sig-line" />
                        <div className="cert-sig-name">Customer Signature</div>
                        <div className="cert-sig-title">{form.name}</div>
                      </div>
                    </div>

                    {/* Footer */}
                    <div className="cert-doc-footer">
                      <p>
                        This certificate is for reference purposes only and does not constitute a legally binding
                        agreement or guarantee of allotment. Final booking is subject to availability and execution
                        of the official agreement. For queries: 747-747-1006 | info@paradisegroupncr.com
                      </p>
                    </div>

                  </div>
                </div>
              </div>
              {/* End cert-document */}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function getCertPrintStyles() {
  return `
    .cert-border-outer {
      border: 8px double #c8a84b;
      margin: 20px; padding: 6px;
      min-height: 95vh;
    }
    .cert-border-inner {
      border: 2px solid #c8a84b;
      padding: 28px 36px;
      position: relative; overflow: hidden;
    }
    .cert-company-header {
      display: flex; align-items: center; gap: 20px; margin-bottom: 8px;
    }
    .cert-logo { width: 70px; height: 70px; object-fit: contain; }
    .cert-company-name { font-size: 22px; font-weight: 800; color: #0d1b2a; letter-spacing: 1px; }
    .cert-company-tagline { font-size: 13px; color: #c8a84b; font-style: italic; }
    .cert-company-contact { font-size: 12px; color: #555; margin-top: 4px; }
    .cert-divider-gold { border: none; border-top: 3px solid #c8a84b; margin: 16px 0; }
    .cert-divider-gold.thin { border-top-width: 1px; margin: 14px 0; }
    .cert-title-block { text-align: center; position: relative; padding: 10px 0; }
    .cert-watermark {
      position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%) rotate(-30deg);
      font-size: 80px; font-weight: 900; color: rgba(200,168,75,0.08);
      letter-spacing: 8px; white-space: nowrap; pointer-events: none;
    }
    .cert-title-label { font-size: 13px; letter-spacing: 4px; color: #999; text-transform: uppercase; }
    .cert-title-main { font-size: 32px; font-weight: 900; color: #0d1b2a; letter-spacing: 2px; margin: 4px 0; }
    .cert-ref-row { display: flex; justify-content: space-between; font-size: 12px; color: #777; margin-top: 8px; }
    .cert-body-text { text-align: center; color: #333; font-size: 14.5px; line-height: 1.7; margin: 16px 0; }
    .cert-details-grid { background: #fafafa; border: 1px solid #e5e7eb; border-radius: 8px; padding: 14px 20px; margin: 14px 0; }
    .cert-detail-row { display: flex; justify-content: space-between; padding: 7px 0; border-bottom: 1px dashed #e5e7eb; font-size: 13.5px; }
    .cert-detail-row:last-child { border-bottom: none; }
    .cert-detail-label { font-weight: 700; color: #444; min-width: 150px; }
    .cert-detail-val { color: #0d1b2a; font-weight: 600; text-align: right; }
    .cert-signatures { display: flex; justify-content: space-between; align-items: center; margin: 24px 0 16px; }
    .cert-sig-block { text-align: center; flex: 1; }
    .cert-sig-line { border-top: 1.5px solid #0d1b2a; width: 160px; margin: 0 auto 6px; }
    .cert-sig-name { font-weight: 700; font-size: 13px; color: #0d1b2a; }
    .cert-sig-title { font-size: 11.5px; color: #777; }
    .cert-stamp { text-align: center; }
    .cert-stamp-circle {
      width: 90px; height: 90px; border-radius: 50%;
      border: 4px double #c8a84b;
      display: flex; align-items: center; justify-content: center;
      margin: 0 auto;
    }
    .cert-stamp-inner { text-align: center; }
    .cert-stamp-pg { font-size: 22px; font-weight: 900; color: #c8a84b; line-height: 1; }
    .cert-stamp-ncr { font-size: 11px; color: #0d1b2a; font-weight: 700; letter-spacing: 2px; }
    .cert-doc-footer { font-size: 10px; color: #999; text-align: center; line-height: 1.5; margin-top: 10px; }
  `;
}
